import React, { useState } from "react";

import { Edit, Plus, Trash2 } from "lucide-react";

import { Button } from "~/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/components/ui/table";
import { toast } from "~/hooks/use-toast";
import { SalesChannel, useSalesChannels } from "~/hooks/useSalesChannels";

interface SalesChannelManagementModalProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
}

const SalesChannelManagementModal: React.FC<
  SalesChannelManagementModalProps
> = ({ isOpen, onOpenChange }) => {
  const {
    salesChannels,
    isLoading,
    createSalesChannel,
    updateSalesChannel,
    deleteSalesChannel,
    isCreating,
    isUpdating,
    isDeleting,
  } = useSalesChannels();

  const [newChannelName, setNewChannelName] = useState("");
  const [editingChannel, setEditingChannel] = useState<SalesChannel | null>(
    null
  );
  const [editName, setEditName] = useState("");

  const handleCreate = () => {
    if (!newChannelName.trim()) {
      toast({
        title: "Error",
        description: "El nombre del canal de venta es requerido.",
        variant: "destructive",
      });
      return;
    }

    createSalesChannel(newChannelName.trim());
    setNewChannelName("");
  };

  const handleEdit = (channel: SalesChannel) => {
    setEditingChannel(channel);
    setEditName(channel.name);
  };

  const handleUpdate = () => {
    if (!editingChannel || !editName.trim()) {
      toast({
        title: "Error",
        description: "El nombre del canal de venta es requerido.",
        variant: "destructive",
      });
      return;
    }

    updateSalesChannel({ id: editingChannel.id, name: editName.trim() });
    setEditingChannel(null);
    setEditName("");
  };

  const handleDelete = (channel: SalesChannel) => {
    if (
      window.confirm(
        `¿Está seguro de que desea eliminar el canal "${channel.name}"? Esta acción no se puede deshacer.`
      )
    ) {
      deleteSalesChannel(channel.id);
    }
  };

  const cancelEdit = () => {
    setEditingChannel(null);
    setEditName("");
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>Gestionar Canales de Venta</DialogTitle>
        </DialogHeader>

        {/* Create New Channel */}
        <div className="space-y-4">
          <div className="flex gap-2">
            <div className="flex-1">
              <Label htmlFor="new-channel">Nuevo Canal de Venta</Label>
              <Input
                id="new-channel"
                value={newChannelName}
                onChange={(e) => setNewChannelName(e.target.value)}
                placeholder="Nombre del canal"
                onKeyPress={(e) => {
                  if (e.key === "Enter") {
                    handleCreate();
                  }
                }}
              />
            </div>
            <div className="flex items-end">
              <Button
                onClick={handleCreate}
                disabled={isCreating || !newChannelName.trim()}
                size="sm"
              >
                <Plus className="mr-1 h-4 w-4" />
                Crear
              </Button>
            </div>
          </div>

          {/* Channels Table */}
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nombre</TableHead>
                  <TableHead className="text-right">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={2} className="text-center">
                      Cargando...
                    </TableCell>
                  </TableRow>
                ) : salesChannels.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={2} className="text-center">
                      No hay canales de venta
                    </TableCell>
                  </TableRow>
                ) : (
                  salesChannels.map((channel, index) => (
                    <TableRow
                      key={channel.id}
                      className="duration-300 animate-in fade-in-0 slide-in-from-top-1 fill-mode-both"
                      style={{
                        animationDelay: `${Math.min(index * 40, 400)}ms`,
                      }}
                    >
                      <TableCell>
                        {editingChannel?.id === channel.id ? (
                          <Input
                            value={editName}
                            onChange={(e) => setEditName(e.target.value)}
                            onKeyPress={(e) => {
                              if (e.key === "Enter") {
                                handleUpdate();
                              } else if (e.key === "Escape") {
                                cancelEdit();
                              }
                            }}
                            autoFocus
                          />
                        ) : (
                          channel.name
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        {editingChannel?.id === channel.id ? (
                          <div className="space-x-2">
                            <Button
                              onClick={handleUpdate}
                              disabled={isUpdating}
                              size="sm"
                              variant="outline"
                            >
                              Guardar
                            </Button>
                            <Button
                              onClick={cancelEdit}
                              size="sm"
                              variant="ghost"
                            >
                              Cancelar
                            </Button>
                          </div>
                        ) : (
                          <div className="space-x-2">
                            <Button
                              onClick={() => handleEdit(channel)}
                              size="sm"
                              variant="ghost"
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button
                              onClick={() => handleDelete(channel)}
                              disabled={isDeleting}
                              size="sm"
                              variant="ghost"
                              className="text-red-600 hover:text-red-700"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SalesChannelManagementModal;
